import { usePostHog } from '#Root/usePostHog';
import { useMixpanelContext } from 'context/MixpanelProvider/useMixpanelContext';
import React from 'react';
import {
  Link as ReactRouterLink,
  type LinkProps as ReactRouterLinkProps,
} from 'react-router';
import { assignLocation } from 'utils/assignLocation';
import { normalizeTelemetryEvent } from 'utils/normalizeTelemetryEvent';
import { isUrl } from './isUrl';

interface LinkProps extends ReactRouterLinkProps {
  trackingName?: string;
  trackingMeta?: Record<string, unknown>;
  /**
   * `preventDefault` is a hacky way to allow us to have Links that fire
   * tracking events but do not programmatically navigate afterwards. The
   * intended use case is links that should have a destination as a fallback
   * for a programmatic behavior, such as a "contact support" link that
   * normally should open the messenger, but falls back to navigating to the
   * help center.
   */
  preventDefault?: boolean;
}

/**
 * Render a standard link or a client-side routing link, with some helpful
 * extras to make solving common problems easier.
 *
 * This component can be used directly, but is primarily intended as a basis
 * for other components that need certain functionality.
 */
const Link = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const trackClick = useTrackingHandler(props);

  // The tracking props aren't valid attributes to pass on to the anchor, so
  // remove them.
  const { trackingName, trackingMeta, preventDefault, ...rest } = props;
  return (
    <ReactRouterLink
      ref={ref}
      {...rest}
      rel={rest.target === '_blank' ? 'noreferrer' : rest.rel}
      onClick={(e) => {
        if (preventDefault) {
          e.preventDefault();
        }

        trackClick?.(e);
        rest?.onClick?.(e);

        // Prevent navigation during test runs when requested
        if (window.carrot.preventTestNavigation) {
          e.preventDefault();
        }
      }}
    />
  );
});
Link.displayName = 'Link';

function useTrackingHandler({
  to,
  target,
  trackingName,
  trackingMeta,
  preventDefault,
}: LinkProps) {
  const mixpanel = useMixpanelContext();
  const posthog = usePostHog();

  if (!trackingName) {
    return undefined;
  }

  return (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const willOpenInNewTab =
      target === '_blank' || e.ctrlKey || e.metaKey || e.shiftKey;
    const raceCondition = isUrl(to) && !willOpenInNewTab;
    const normalizedEventData = normalizeTelemetryEvent(
      trackingName,
      trackingMeta
    );

    /**
     * Prevent navigation until the tracking events have been sent.
     *
     * This can happen when the link is to an external resource (not a
     * client-side route) and the link is not being opened in a new tab/window.
     * This creates a race condition where the current page may be unloaded to
     * navigate to the external page before the tracking occurs.
     *
     * We don't need to worry about this if the link is to a client-side route,
     * is set to open in a new tab via its target, or if the user invoked the
     * link while holding a modifier key (ctrl/shift/cmd). In these cases, the
     * page is not unloaded.
     */
    if (raceCondition) {
      e.preventDefault();

      // Send PostHog event, using beacon transport to hopefully log the event
      // reliably. The navigation will be handled by the Mixpanel callback.
      posthog.capture(
        normalizedEventData.postHog.name,
        normalizedEventData.postHog.meta,
        { transport: 'sendBeacon' }
      );

      // Send Mixpanel event
      mixpanel.track(
        normalizedEventData.mixpanel.name,
        normalizedEventData.mixpanel.meta,
        // Disable Mixpanel's default batching/queuing and send the event
        // immediately. This ensures the navigation callback is run after the
        // network request has actually been sent, rather than after the event
        // has been batched.
        // See https://github.com/mixpanel/mixpanel-js/issues/433#issuecomment-2231296633.
        { send_immediately: true },
        // Provide Mixpanel with a callback that navigates to the link
        // destination after the tracking event has been sent.
        () => {
          if (preventDefault) {
            return;
          }

          assignLocation(to);
        }
      );
    } else {
      posthog.capture(
        normalizedEventData.postHog.name,
        normalizedEventData.postHog.meta
      );
      mixpanel.track(
        normalizedEventData.mixpanel.name,
        normalizedEventData.mixpanel.meta
      );
    }
  };
}

export { Link };
export type { LinkProps };
